import type { ReactElement } from 'react';
import { SUPPORT_EMAIL, supportMailto } from '@/lib/support';

interface SupportLinkProps {
  /**
   * `inline` — small text link rendered below CTAs in error/empty surfaces.
   *   Default label: "Need help? Contact support".
   * `footer` — terminal-style item (`SUPPORT: support@arcanumhq.com`) for
   *   `<StatusFooter>`. Matches the existing `REGION: us-east-1` rhythm.
   */
  variant: 'inline' | 'footer';
  /** Optional prefilled email subject — surfaces context to support. */
  subject?: string;
}

/**
 * Single primitive for surfacing the support mailto across the app. Always
 * resolves the address via `lib/support.ts` so we never re-inline a literal
 * email. See `lib/support.ts` for the env-driven SUPPORT_EMAIL resolution.
 */
export function SupportLink({
  variant,
  subject,
}: SupportLinkProps): ReactElement {
  const href = supportMailto({ subject });

  if (variant === 'footer') {
    return (
      <a
        href={href}
        data-testid="support-link-footer"
        className="text-[8px] font-mono uppercase tracking-[0.5px] text-text-dim hover:text-text transition-colors"
      >
        SUPPORT: {SUPPORT_EMAIL}
      </a>
    );
  }

  return (
    <a
      href={href}
      data-testid="support-link-inline"
      className="text-xs font-mono text-text-muted hover:text-accent transition-colors underline-offset-2 hover:underline"
    >
      Need help? Contact support
    </a>
  );
}
